import React, { useEffect, useMemo, useState } from 'react';
import { Bell } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from '../axios';

interface NotificationItem {
  id: string;
  type: 'LEAD_PROGRESS' | 'STALE_DEAL_REMINDER' | 'PASSWORD_RESET';
  title: string;
  message: string;
  eventKey?: string | null;
  link?: string | null;
  read: boolean;
  createdAt: string;
}

interface NotificationBellProps {
  currentLanguage: string;
  isAuthenticated: boolean;
}

const NotificationBell: React.FC<NotificationBellProps> = ({ currentLanguage, isAuthenticated }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchNotifications = async () => {
    if (!isAuthenticated) return;
    const [listRes, unreadRes] = await Promise.all([
      axios.get('/api/notifications', { params: { limit: 10 } }),
      axios.get('/api/notifications/unread-count'),
    ]);
    setItems(listRes.data || []);
    setUnreadCount(Number(unreadRes.data || 0));
  };

  useEffect(() => {
    if (!isAuthenticated) {
      setItems([]);
      setUnreadCount(0);
      return;
    }
    fetchNotifications();
    const timer = setInterval(fetchNotifications, 60000);
    return () => clearInterval(timer);
  }, [isAuthenticated]);

  const formatNotificationPath = (link?: string | null) => {
    if (!link) return null;
    if (link.startsWith('/login') || link.startsWith('/reset-password')) {
      return link;
    }
    return `/${currentLanguage}${link}`;
  };

  const markReadAndNavigate = async (item: NotificationItem) => {
    await axios.post(`/api/notifications/${item.id}/read`);
    await fetchNotifications();
    setOpen(false);
    const path = formatNotificationPath(item.link);
    if (path) {
      navigate(path);
    }
  };

  const markAllRead = async () => {
    await axios.post('/api/notifications/read-all');
    await fetchNotifications();
  };

  const parseLeadRejected = (message: string) => {
    const match = message.match(/^(.*) was not progressed\.$/);
    return match ? { company: match[1] } : null;
  };

  const parseLeadStatus = (message: string) => {
    const match = message.match(/^(.*) moved to .+\.$/);
    return match ? { company: match[1] } : null;
  };

  const parseStaleDeal = (message: string) => {
    const match = message.match(/^(.*) has had no progress for (\d+) days\.$/);
    return match ? { company: match[1], days: Number(match[2]) } : null;
  };

  const localizeNotification = (item: NotificationItem) => {
    const eventKey = item.eventKey ?? '';

    if (eventKey === 'password-reset:admin' || item.type === 'PASSWORD_RESET') {
      return {
        title: t('notifications.events.passwordReset.title'),
        message: t('notifications.events.passwordReset.message'),
      };
    }

    if (eventKey === 'lead-progress:rejected') {
      const parsed = parseLeadRejected(item.message);
      if (parsed) {
        return {
          title: t('notifications.events.leadUpdate.title'),
          message: t('notifications.events.leadUpdate.rejected', { company: parsed.company }),
        };
      }
    }

    if (eventKey.startsWith('lead-progress:status:')) {
      const status = eventKey.slice('lead-progress:status:'.length);
      const parsed = parseLeadStatus(item.message);
      if (parsed) {
        return {
          title: t('notifications.events.leadUpdate.title'),
          message: t('notifications.events.leadUpdate.statusChanged', {
            company: parsed.company,
            status: t(`partnerStatus.deal.${status}`, t(`status.deal.${status}`, status)),
          }),
        };
      }
    }

    if (eventKey.startsWith('stale-deal:')) {
      const parsed = parseStaleDeal(item.message);
      if (parsed) {
        return {
          title: t('notifications.events.staleDeal.title'),
          message: t('notifications.events.staleDeal.message', {
            company: parsed.company,
            days: parsed.days,
          }),
        };
      }
    }

    if (item.type === 'LEAD_PROGRESS') {
      const rejected = parseLeadRejected(item.message);
      if (rejected) {
        return {
          title: t('notifications.events.leadUpdate.title'),
          message: t('notifications.events.leadUpdate.rejected', { company: rejected.company }),
        };
      }

      const statusMatch = parseLeadStatus(item.message);
      if (statusMatch) {
        return {
          title: t('notifications.events.leadUpdate.title'),
          message: t('notifications.events.leadUpdate.generic', { company: statusMatch.company }),
        };
      }
    }

    if (item.type === 'STALE_DEAL_REMINDER') {
      const parsed = parseStaleDeal(item.message);
      if (parsed) {
        return {
          title: t('notifications.events.staleDeal.title'),
          message: t('notifications.events.staleDeal.message', {
            company: parsed.company,
            days: parsed.days,
          }),
        };
      }
    }

    return { title: item.title, message: item.message };
  };

  const formattedItems = useMemo(
    () => items.map((item) => ({
      ...item,
      ...localizeNotification(item),
      timestamp: new Intl.DateTimeFormat(i18n.resolvedLanguage, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(item.createdAt)),
    })),
    [items, i18n.resolvedLanguage]
  );

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((prev) => !prev)}
        className="relative flex items-center gap-2 font-bold text-white hover:text-[#47ABDB] transition-colors duration-300"
        aria-label={t('notifications.open')}
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span className="absolute -top-2 -right-2 min-w-5 h-5 rounded-full bg-red-600 px-1 text-[10px] leading-5 text-white text-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>
      {open && (
        <div className="lg:absolute lg:right-0 mt-2 w-[22rem] max-w-[calc(100vw-2rem)] bg-white rounded-md shadow-lg py-1 z-50 text-left">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <div>
              <div className="text-sm font-semibold text-[#111D4E]">{t('notifications.title')}</div>
              <div className="text-xs text-slate-500">{t('notifications.subtitle')}</div>
            </div>
            <button onClick={markAllRead} className="text-xs font-semibold text-[#47ABDB] hover:underline">
              {t('notifications.markAllRead')}
            </button>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {formattedItems.length === 0 ? (
              <div className="px-4 py-6 text-sm text-slate-500">{t('notifications.empty')}</div>
            ) : (
              formattedItems.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => markReadAndNavigate(item)}
                  className={`w-full px-4 py-3 text-left border-b border-slate-100 hover:bg-slate-50 ${item.read ? 'bg-white' : 'bg-sky-50/60'}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-sm font-semibold text-[#111D4E]">{item.title}</div>
                      <div className="mt-1 text-sm text-slate-600">{item.message}</div>
                    </div>
                    {!item.read && <span className="mt-1 h-2.5 w-2.5 rounded-full bg-[#47ABDB]" />}
                  </div>
                  <div className="mt-2 text-xs text-slate-500">{item.timestamp}</div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
